import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCardIcon, BuildingIcon, SmartphoneIcon, InfoIcon } from 'lucide-react';
import { formatNaira } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface FundWalletProps {
  navigate: (screen: string) => void;
}
export function FundWallet({
  navigate
}: FundWalletProps) {
  const [amount, setAmount] = useState<string>('');
  const [method, setMethod] = useState<'card' | 'bank' | 'ussd'>('card');
  const quickAmounts = [5000, 10000, 20000, 50000, 100000];
  const numAmount = amount ? parseInt(amount.replace(/,/g, '')) : 0;
  const fee = method === 'card' ? numAmount * 0.015 : 0;
  const total = numAmount + fee;
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val) {
      setAmount(parseInt(val).toLocaleString('en-US'));
    } else {
      setAmount('');
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 20
  }} className="flex-1 overflow-y-auto pb-safe bg-slate-50 flex flex-col h-full" data-id="element-474">
      <TopBar title="Fund Wallet" showBack onBack={() => navigate('wallet')} data-id="element-475" />

      <div className="px-5 pt-6 pb-6 flex-1 flex flex-col" data-id="element-476">
        {/* Amount Input */}
        <div className="text-center mb-8" data-id="element-477">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-478">
            Enter Amount
          </p>
          <div className="flex items-center justify-center text-5xl font-bold text-[#1B3B36] mb-6" data-id="element-479">
            <span className="text-gray-400 mr-1" data-id="element-480">₦</span>
            <input type="text" value={amount} onChange={handleAmountChange} placeholder="0" className="bg-transparent w-full max-w-[200px] text-center focus:outline-none placeholder-gray-300" autoFocus data-id="element-481" />
          </div>

          {/* Quick Amounts */}
          <div className="flex flex-wrap justify-center gap-2" data-id="element-482">
            {quickAmounts.map(amt => <button key={amt} onClick={() => setAmount(amt.toLocaleString('en-US'))} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-bold text-[#1B3B36] shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-95" data-id="element-483">
                {formatNaira(amt)}
              </button>)}
          </div>
        </div>

        {/* Payment Methods */}
        <div className="mb-8" data-id="element-484">
          <h3 className="text-sm font-bold text-[#1B3B36] mb-3 px-1" data-id="element-485">
            Payment Method
          </h3>
          <div className="space-y-3" data-id="element-486">
            <div onClick={() => setMethod('card')} className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-4 ${method === 'card' ? 'border-amber-500 bg-amber-50/30 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-487">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${method === 'card' ? 'bg-amber-100 text-amber-600' : 'bg-gray-50 text-gray-500'}`} data-id="element-488">
                <CreditCardIcon className="w-5 h-5" data-id="element-489" />
              </div>
              <div className="flex-1" data-id="element-490">
                <h4 className="font-bold text-[#1B3B36] text-sm" data-id="element-491">
                  Paystack (Card/Bank)
                </h4>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-492">
                  Instant • 1.5% fee
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${method === 'card' ? 'border-amber-500' : 'border-gray-300'}`} data-id="element-493">
                {method === 'card' && <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" data-id="element-494"></div>}
              </div>
            </div>

            <div onClick={() => setMethod('bank')} className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-4 ${method === 'bank' ? 'border-amber-500 bg-amber-50/30 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-495">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${method === 'bank' ? 'bg-amber-100 text-amber-600' : 'bg-gray-50 text-gray-500'}`} data-id="element-496">
                <BuildingIcon className="w-5 h-5" data-id="element-497" />
              </div>
              <div className="flex-1" data-id="element-498">
                <h4 className="font-bold text-[#1B3B36] text-sm" data-id="element-499">
                  Bank Transfer
                </h4>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-500">
                  Free • Takes 1-3 hours
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${method === 'bank' ? 'border-amber-500' : 'border-gray-300'}`} data-id="element-501">
                {method === 'bank' && <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" data-id="element-502"></div>}
              </div>
            </div>

            <div onClick={() => setMethod('ussd')} className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-4 ${method === 'ussd' ? 'border-amber-500 bg-amber-50/30 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-503">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${method === 'ussd' ? 'bg-amber-100 text-amber-600' : 'bg-gray-50 text-gray-500'}`} data-id="element-504">
                <SmartphoneIcon className="w-5 h-5" data-id="element-505" />
              </div>
              <div className="flex-1" data-id="element-506">
                <h4 className="font-bold text-[#1B3B36] text-sm" data-id="element-507">USSD</h4>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-508">
                  Dial *737# • Free
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${method === 'ussd' ? 'border-amber-500' : 'border-gray-300'}`} data-id="element-509">
                {method === 'ussd' && <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" data-id="element-510"></div>}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-auto" data-id="element-511">
          {/* Summary */}
          {numAmount > 0 && <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm mb-6" data-id="element-512">
              <div className="flex justify-between text-sm mb-2" data-id="element-513">
                <span className="text-gray-500 font-medium" data-id="element-514">Amount</span>
                <span className="font-bold text-[#1B3B36]" data-id="element-515">
                  {formatNaira(numAmount)}
                </span>
              </div>
              <div className="flex justify-between text-sm mb-3 pb-3 border-b border-gray-50" data-id="element-516">
                <span className="text-gray-500 font-medium flex items-center gap-1" data-id="element-517">
                  Fee <InfoIcon className="w-3 h-3" data-id="element-518" />
                </span>
                <span className="font-bold text-[#1B3B36]" data-id="element-519">
                  {fee > 0 ? formatNaira(fee) : 'Free'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-520">
                <span className="font-bold text-[#1B3B36]" data-id="element-521">Total to Pay</span>
                <span className="font-bold text-lg text-amber-600" data-id="element-522">
                  {formatNaira(total)}
                </span>
              </div>
            </div>}

          <button disabled={numAmount === 0} onClick={() => navigate('wallet')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all active:scale-95 ${numAmount > 0 ? 'bg-amber-500 hover:bg-amber-400 text-[#1B3B36] shadow-lg shadow-amber-500/30' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-523">
            Fund Wallet
          </button>
        </div>
      </div>
    </motion.div>;
}